<nz-drawer
  (nzOnClose)="closeDrawer()"
  [nzClosable]="true"
  [nzTitle]="drawerTitleTpl"
  [nzVisible]="show"
  [nzWidth]="1500"
>
  <ng-template #drawerTitleTpl>
    <span class="fe fe-edit font-semibold"></span> Edit Place Details
  </ng-template>

  <form *nzDrawerContent [formGroup]="form" nz-form nzLayout="vertical" class="h-full">
    <div class="grid grid-cols-12 gap-6 h-full">
      <!-- Left column - Controls -->
      <div class="col-span-4">
        <nz-alert
          nzType="info"
          class="my-4"
          nzMessage="First Search for the Place"
          [nzDescription]="
            searchPerformed()
              ? 'Place found! You can now edit the details below.'
              : 'To edit this place, you must first search for it above. This will ensure accurate location data and enable the form fields below.'
          "
          nzShowIcon="true"
        >
        </nz-alert>
        <nz-alert
          nzType="warning"
          class="my-4"
          nzMessage="Changes apply everywhere, immediately"
          [nzDescription]="impactDescription()"
          nzShowIcon="true"
        >
        </nz-alert>
        <app-error-display *ngIf="showError() && errorDetails" [error]="errorDetails" (dismiss)="onDismissError()" (retry)="saveChanges()"></app-error-display>

        <div class="mt-4">
          <nz-form-item>
            <nz-form-label [nzRequired]="true">
              <div class="flex items-center">
                <span class="fe fe-search mr-2"></span>
                <span>Search for Place</span>
              </div>
            </nz-form-label>
            <nz-form-control>
              <buspro-admin-place-autocomplete
                placeholder="Start typing a place name or address..."
                [options]="placeOptions"
                (placeSelected)="handleAddressChange($event)"
                (inputChanged)="onInputChanged($event)"
                class="w-full"
              />
            </nz-form-control>
          </nz-form-item>

          <div [class.opacity-50]="!formEnabled()">
            <nz-form-item>
              <nz-form-label [nzRequired]="true">Display Name</nz-form-label>
              <nz-form-control>
                <input
                  formControlName="name"
                  nz-input
                  placeholder="Place name"
                  type="text"
                />
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label [nzRequired]="true"
                >Select Place Type</nz-form-label
              >
              <nz-form-control>
                <nz-radio-group formControlName="placeType">
                  <label
                    [nzValue]="terminalTypesEnum.Terminal"
                    nz-radio
                    nz-tooltip
                    nzTooltipTitle="Where a route starts or ends. Terminals can also be used as intermediate stops in routes."
                    >Terminal</label
                  >
                  <label
                    [nzValue]="terminalTypesEnum.BusStop"
                    nz-radio
                    nz-tooltip
                    nzTooltipTitle="These are places where buses pass but do not end or start trips from"
                    >Bus Stop</label
                  >
                </nz-radio-group>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label [nzRequired]="true"
                >Geofence radius (meters)</nz-form-label
              >
              <nz-form-control>
                <!-- Preset buttons -->
                <div class="flex flex-wrap gap-2 mb-3" *ngIf="formEnabled()">
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.busStop)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.busStop"
                  >
                    Bus Stop (800m)
                  </button>
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.terminal)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.terminal"
                  >
                    Terminal (1500m)
                  </button>
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.busStop2x)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.busStop2x"
                  >
                    2x Bus Stop (1600m)
                  </button>
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.terminal2x)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.terminal2x"
                  >
                    2x Terminal (3000m)
                  </button>
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.busStop3x)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.busStop3x"
                  >
                    3x Bus Stop (2400m)
                  </button>
                  <button
                    type="button"
                    (click)="applyPreset(PRESETS.terminal3x)"
                    class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                    [class.bg-blue-100]="radius?.value === PRESETS.terminal3x"
                  >
                    3x Terminal (4500m)
                  </button>
                </div>
                <ngx-slider
                  [value]="form.get('radius')?.value || 150"
                  [options]="sliderOptions"
                  (valueChange)="circleRadiusChanged($event)"
                ></ngx-slider>
                <div *ngIf="formEnabled()" class="text-xs text-gray-500 mt-2">
                  Current: {{ radius?.value || 150 }}m
                  <span *ngIf="(radius?.value || 0) >= 1000">
                    ({{ ((radius?.value || 0) / 1000).toFixed(1) }} km)
                  </span>
                </div>
              </nz-form-control>
            </nz-form-item>
          </div>

          <!-- Action buttons -->
          <div class="flex justify-end space-x-2 mt-10">
            <button
              (click)="closeDrawer()"
              class="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              (click)="saveChanges()"
              [disabled]="form.invalid || !searchPerformed() || mutation.isPending()"
              [class.loading]="mutation.isPending()"
              class="btn btn-primary"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <!-- Right column - Map -->
      <div class="col-span-8 h-full" *ngIf="name?.value">
        <div class="relative h-full">
          <google-map
            (mapInitialized)="onMapInit($event)"
            [center]="center"
            [zoom]="zoom"
            height="100%"
            width="100%"
          >
            @for (c of circles; track c.id){
            <map-circle
              [center]="c.center"
              [options]="{
                fillColor: c.fillColor,
                fillOpacity: c.fillOpacity,
                strokeColor: c.strokeColor,
                strokeOpacity: c.strokeOpacity,
                strokeWeight: c.strokeWeight,
                editable: false,
                draggable: false,
                clickable: true,
                zIndex: c.zIndex
              }"
              [radius]="c.radius"
              (circleRadiusChange)="circleRadiusChanged($event)"
              (circleCenterChange)="circleCenterChanged($event)"
            />
            } @for (m of markers; track m.id){
            <map-marker
              [options]="m.options"
              [label]="name!.value!"
              [position]="m.position"
              [title]="m.title"
            />
            }
          </google-map>

          <!-- Custom radius control panel -->
          <div
            class="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg z-10"
          >
            <div class="flex flex-col">
              <h3 class="text-lg font-medium mb-2">Adjust Radius</h3>
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm">Fine adjust:</span>
                <div class="flex items-center space-x-2">
                  <button
                    class="btn btn-xs btn-circle"
                    (click)="adjustRadius(-50)"
                    [disabled]="(radius?.value || 0) <= 100"
                  >
                    <span class="fe fe-minus"></span>
                  </button>
                  <button
                    class="btn btn-xs btn-circle"
                    (click)="adjustRadius(50)"
                  >
                    <span class="fe fe-plus"></span>
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm">Coarse adjust:</span>
                <div class="flex items-center space-x-2">
                  <button
                    class="btn btn-xs btn-circle"
                    (click)="adjustRadius(-500)"
                    [disabled]="(radius?.value || 0) <= 550"
                  >
                    <span class="fe fe-minus"></span>
                  </button>
                  <button
                    class="btn btn-xs btn-circle"
                    (click)="adjustRadius(500)"
                  >
                    <span class="fe fe-plus"></span>
                  </button>
                </div>
              </div>
              <div class="mt-2 text-center font-medium">
                {{ radius?.value || 0 }} meters
              </div>
              <div class="mt-1 text-xs text-gray-500 text-center">
                Current radius
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</nz-drawer>
